import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

/**
 * HapticFeedback service provides methods to trigger haptic feedback on supported devices.
 * This enhances the user experience by providing tactile feedback for important actions and analytics.
 */
class HapticFeedback {
  /**
   * Trigger a short haptic tap.
   * Preferred cross‑platform helper for subtle interactions.
   * Falls back to navigator.vibrate(20) on supported browsers.
   * @returns {Promise<void>}
   */
  async short() {
    try {
      if (this.hasHaptics()) {
        await Haptics.impact({ style: ImpactStyle.Light });
        return;
      }
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        navigator.vibrate(20);
      }
    } catch (error) {
      console.warn('Haptic feedback not available:', error);
    }
  }

  /** Trigger a light haptic feedback (short vibration). */
  async light() {
    try {
      if (this.hasHaptics()) {
        await Haptics.impact({ style: ImpactStyle.Light });
      } else if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        navigator.vibrate(20);
      }
    } catch (error) {
      console.warn('Haptic feedback not available:', error);
    }
  }

  /** Trigger a medium haptic feedback. */
  async medium() {
    try {
      if (this.isSupported()) {
        await Haptics.impact({ style: ImpactStyle.Medium });
      }
    } catch (error) {
      console.warn('Haptic feedback not available:', error);
    }
  }

  /** Trigger a heavy haptic feedback. */
  async heavy() {
    try {
      if (this.isSupported()) {
        await Haptics.impact({ style: ImpactStyle.Heavy });
      }
    } catch (error) {
      console.warn('Haptic feedback not available:', error);
    }
  }

  /** Trigger a success haptic feedback pattern. */
  async success() {
    try {
      if (this.isSupported()) {
        await Haptics.notification({ type: NotificationType.Success });
      }
    } catch (error) {
      console.warn('Haptic feedback not available:', error);
    }
  }

  /** Trigger a warning haptic feedback pattern. */
  async warning() {
    try {
      if (this.isSupported()) {
        await Haptics.notification({ type: NotificationType.Warning });
      }
    } catch (error) {
      console.warn('Haptic feedback not available:', error);
    }
  }

  /** Trigger an error haptic feedback pattern. */
  async error() {
    try {
      if (this.isSupported()) {
        await Haptics.notification({ type: NotificationType.Error });
      }
    } catch (error) {
      console.warn('Haptic feedback not available:', error);
    }
  }

  /**
   * Internal: checks if Capacitor Haptics plugin is available on this platform.
   * @returns {boolean}
   */
  hasHaptics() {
    try {
      return Capacitor?.isPluginAvailable?.('Haptics') === true;
    } catch (_) {
      return false;
    }
  }

  /**
   * Returns true if either Capacitor Haptics is available or the Web Vibration API exists.
   * @returns {boolean}
   */
  isSupported() {
    if (this.hasHaptics()) return true;
    try {
      return typeof navigator !== 'undefined' && 'vibrate' in navigator;
    } catch (_) {
      return false;
    }
  }
}

export const hapticFeedback = new HapticFeedback();
export const triggerShortHaptic = () => hapticFeedback.short();